/**
 * IBAS Partners data — single source of truth for the /partners page
 * (Academic Partners · Learning Center Partners · Agents).
 *
 * SOURCING (ibas-brand skill, HARD rule): every institutional fact here comes
 * ONLY from the branding folder, https://ibas.edu.eu, or the user — nothing is
 * invented. Where a fact is not verified it is LEFT OUT, never guessed.
 *
 * Sources:
 *   • Partners overview:        https://ibas.edu.eu/about/partners/
 *   • Academic Partners:        https://ibas.edu.eu/about/academic-partners/
 *   • Learning Center Partners: https://ibas.edu.eu/about/learning-center-partners/
 *     (+ each centre's dedicated page under /about/campus-partners/,
 *     /about/franchise-partners/ and /about/learning-center-partners/)
 *   • Agents:                   https://ibas.edu.eu/about/agents/
 *
 * Compliance notes:
 *   • The Academic Partners list is taken from the DEDICATED academic-partners
 *     page (the authoritative categorisation). Bangkok School of Management and
 *     IBA Egypt appear only on the partners landing carousel without a clear
 *     category, so they are deliberately omitted rather than filed under a guess.
 *   • Agents publish personal Gmail addresses / personal phone numbers on the
 *     live site. Those third-party personal contacts are NOT reproduced here
 *     (privacy). Enquiries are routed through IBAS admissions, and only the
 *     official IBAS role alias (india@ibas.edu.eu) is surfaced where one exists.
 *   • "Learning Center" keeps the source's US spelling in the section/label name
 *     because it is the partner-programme's proper name as published; running
 *     prose uses UK English ("programme", "centre", "recognised").
 */

import {
  Award,
  BookOpen,
  Building2,
  Compass,
  GraduationCap,
  Handshake,
  Landmark,
  MapPin,
  Network,
  Rocket,
  Sparkles,
  UserCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════
 * 0 · PARTNERS OVERVIEW  (source: /about/partners/ + /about/learning-center-partners/)
 * ═══════════════════════════════════════════════════════════════════════════ */

export const PARTNERS_LEDE =
  'IBAS is dedicated to promoting the school’s international and global goals by working with a network of partners around the world — extending Swiss-quality business education far beyond Thalwil.';

/** The four network aims stated on the source page (paraphrased faithfully). */
export const PARTNERS_AIMS: readonly string[] = [
  'Recruit talented students and scholars from abroad',
  'Create study-abroad and learning programmes in English and other languages',
  'Foster partnerships with international institutions',
  'Open interdisciplinary opportunities for scholars abroad',
];

/** High-level summary of the three partner tiers surfaced on this page. */
export interface PartnerTier {
  key: string;
  label: string;
  summary: string;
  Icon: LucideIcon;
  /** In-page anchor. */
  href: string;
}

export const PARTNER_TIERS: readonly PartnerTier[] = [
  {
    key: 'academic',
    label: 'Academic Partners',
    summary: 'Universities and colleges that collaborate with IBAS on academic delivery and progression.',
    Icon: Landmark,
    href: '#academic-partners',
  },
  {
    key: 'learning-center',
    label: 'Learning Center Partners',
    summary: 'Regional centres that host and deliver IBAS learning within their own communities.',
    Icon: Network,
    href: '#learning-center-partners',
  },
  {
    key: 'agents',
    label: 'Agents',
    summary: 'Authorised admissions representatives who guide prospective students to IBAS locally.',
    Icon: UserCheck,
    href: '#agents',
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
 * 1 · ACADEMIC PARTNERS  (source: /about/academic-partners/)
 * ═══════════════════════════════════════════════════════════════════════════ */

export const ACADEMIC_LEDE =
  'Universities and colleges around the world that collaborate with IBAS — broadening the academic reach, mobility and progression open to our students.';

export interface AcademicPartner {
  name: string;
  location: string;
  /** One-line profile drawn from the source. */
  description: string;
  /** Display host (no scheme) — links out to the partner site. */
  website: string;
  href: string;
}

export const ACADEMIC_PARTNERS: readonly AcademicPartner[] = [
  {
    name: 'Solent University',
    location: 'Southampton, United Kingdom',
    description:
      'A public university spanning business, law, the creative industries and maritime studies across five academic faculties.',
    website: 'solent.ac.uk',
    href: 'https://www.solent.ac.uk',
  },
  {
    name: 'VERN University',
    location: 'Zagreb, Croatia',
    description:
      'An international, entrepreneurial university with over twenty years of operation and a UN Global Compact commitment to mobility.',
    website: 'vernuni.eu',
    href: 'https://www.vernuni.eu',
  },
  {
    name: 'Genovasi University College',
    location: 'Malaysia',
    description:
      'Founded in 2012, a pioneering institution integrating design thinking and innovation through hybrid, blended learning.',
    website: 'genovasi.edu.my',
    href: 'https://www.genovasi.edu.my/',
  },
  {
    name: 'Asia e University (AeU)',
    location: 'Malaysia',
    description:
      'A leading Open and Distance Learning university built around accessibility and affordability for learners across the region.',
    website: 'aeu.edu.my',
    href: 'https://aeu.edu.my/',
  },
  {
    name: 'Geomatika University College (GUC)',
    location: 'Malaysia',
    description:
      'Awarded university-college status in 2014, specialising in geospatial technology and land development.',
    website: 'geomatika.edu.my',
    href: 'https://www.geomatika.edu.my/',
  },
  {
    name: 'Kent International College',
    location: 'Vietnam',
    description:
      'Established in 2003 and aligned to the UK QAA quality framework, delivering IT and English programmes to international students.',
    website: 'kent.edu.vn',
    href: 'https://kent.edu.vn/',
  },
  {
    name: 'Hoa Sen University',
    location: 'Ho Chi Minh City, Vietnam',
    description:
      'A private university with ACBSP-recognised business programmes (2015) and a strong emphasis on internationalisation and entrepreneurship.',
    website: 'hoasen.edu.vn',
    href: 'https://www.hoasen.edu.vn',
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
 * 2 · LEARNING CENTER PARTNERS  (source: /about/learning-center-partners/ +
 *     each centre's dedicated page under /about/campus-partners/,
 *     /about/franchise-partners/ and /about/learning-center-partners/)
 *
 *     Every profile paragraph below is transcribed from the centre's own page
 *     on the old site — nothing is invented. ECC Knowledge Group's copy was
 *     written to ECC's specification and is VERBATIM-LOCKED (client rule):
 *     it must never be reworded, shortened or "improved".
 *
 *     Images are either local files under /media (catalogued in
 *     media/manifest.json) or ibas.edu.eu URLs verified HTTP 200.
 * ═══════════════════════════════════════════════════════════════════════════ */

export const LEARNING_CENTER_LEDE =
  'Learning Center Partners host and deliver IBAS learning within their own regions, bringing our programmes closer to students across four continents.';

/** A verified visual — a local /media path or a curl-verified ibas.edu.eu URL. */
export interface LearningCenterImage {
  src: string;
  alt: string;
}

/** One block of a centre's profile copy, as structured on its source page. */
export interface LearningCenterSection {
  /** Heading as published (omitted for a centre's opening block). */
  heading?: string;
  paragraphs?: readonly string[];
  /** Bullet lines rendered as a list (values, majors, imperatives …). */
  list?: readonly string[];
  /** Label → detail pairs rendered as a definition grid (ECTS totals, offices …). */
  facts?: readonly { label: string; detail: string }[];
}

/**
 * The centre's exact "In Collaboration with IBAS" wording. The blurb differs
 * by partnership type (Global Pathways / Standard Edition online / Franchised /
 * local learning center) — each centre's wording is kept verbatim.
 */
export interface LearningCenterCollaboration {
  paragraphs: readonly string[];
  programmes?: readonly string[];
  /** Paragraphs the source publishes after the programme list (announcements). */
  after?: readonly string[];
}

/** Shared heading for the collaboration block, as published on every page. */
export const LEARNING_CENTER_COLLAB_HEADING =
  'In Collaboration with International Business Academy of Switzerland (IBAS)';

export interface LearningCenterPartner {
  name: string;
  country: string;
  /** Detail-page route: /partners/learning-centers/:slug */
  slug: string;
  /** Profile copy, transcribed from the centre's dedicated old-site page. */
  sections: readonly LearningCenterSection[];
  collaboration?: LearningCenterCollaboration;
  /** Published campus/office address lines. */
  locations?: readonly string[];
  /** Institution logo(s), rendered on a white plate. */
  logos?: readonly LearningCenterImage[];
  /** Photo gallery — a centre without photos renders text-only. */
  gallery?: readonly LearningCenterImage[];
  /** The centre's own website(s). */
  websites?: readonly { label: string; href: string }[];
  /** ECC only: copy is contractually locked — never edit it. */
  verbatimLocked?: boolean;
}

/** Learning Center Partners with their full old-site profiles. */
export const LEARNING_CENTER_PARTNERS: readonly LearningCenterPartner[] = [
  {
    name: 'ECC Knowledge Group',
    country: 'United Arab Emirates',
    slug: 'ecc-knowledge-group',
    // ⚠ VERBATIM-LOCKED (client rule): every word of the ECC copy below was
    // written to ECC's specification and MUST NOT be changed.
    verbatimLocked: true,
    sections: [
      {
        paragraphs: [
          'The Official Support Center of IBAS Switzerland in the MENA Region',
          'Supporting learners’ access to IBAS Switzerland’s internationally recognized, Swiss-accredited business programs for executives, entrepreneurs, professionals, and future leaders across the Middle East and North Africa.',
          'Gain a free Edu consultation',
        ],
      },
      {
        heading: 'Cooperation Statement',
        paragraphs: [
          'ECC Knowledge Group is proud to serve as a leading official support center of the International Business Academy of Switzerland – IBAS, supporting learners across the MENA region through professional advising and access to IBAS’s internationally recognized business programs.',
          'This cooperation reflects ECC’s commitment to expanding access to IBAS’s Swiss-accredited business programs and supporting professionals who seek career advancement, leadership development, and doctoral-level academic progression.',
        ],
      },
      {
        heading: 'About ECC Knowledge Group',
        paragraphs: [
          'ECC Knowledge Group is a professional support center dedicated to facilitating regional access to IBAS Switzerland’s advanced business, management, and leadership programs for learners across the MENA region. Headquartered in the United Arab Emirates with a regional office in Egypt.',
        ],
      },
      {
        heading: 'Why ECC Knowledge Group with IBAS?',
        facts: [
          {
            label: 'Regional Leadership',
            detail: 'Strong presence and student reach across Egypt, GCC, and wider MENA markets.',
          },
          {
            label: 'IBAS Program Focus',
            detail:
              'IBAS programs, designed by IBAS, for managers, entrepreneurs, consultants, and senior professionals.',
          },
          {
            label: 'Graduation and Academic Events',
            detail: 'Professional graduation ceremonies, academic visits, and international engagement.',
          },
          {
            label: 'Student Support',
            detail: 'Academic advising and program coordination.',
          },
        ],
      },
      {
        heading:
          'In Collaboration with International Business Academy of Switzerland (IBAS), We deliver the programs :',
      },
      {
        heading: 'Bachelor of Business Administration – (BBA)',
        paragraphs: [
          'The BBA provides a strong foundation in business, management, leadership, marketing, finance, and organizational practices. It is designed for learners who aim to build a professional academic base for future career development or postgraduate progression.',
        ],
      },
      {
        heading: 'Master of Business Administration – (MBA)',
        paragraphs: [
          'The MBA is designed for professionals who seek to strengthen their managerial, strategic, financial, marketing, leadership, and operational capabilities.',
        ],
        facts: [
          { label: 'Total', detail: '60 ECTS' },
          {
            label: 'Core modules',
            detail:
              'covering personal development for leadership and strategic management, organizational behavior, strategic HRM, finance for strategic managers, strategic marketing, strategic planning, international business environment, and research for strategic development',
          },
          { label: 'Specialized modules', detail: 'based on the chosen major' },
          {
            label: 'Suitable for',
            detail: 'professionals, managers, business owners, and future executives',
          },
        ],
      },
      {
        heading: 'MBA Majors include',
        list: [
          'Business Administration',
          'Strategic Management',
          'Human Resource Management',
          'Logistics and Supply Chain Management',
          'Marketing Management',
          'Healthcare Management',
          'Quality Management',
          'Project Management',
          'Accounting and Finance',
          'Digital Transformation',
          'Hospital Management',
          'Entrepreneurial Leadership and Innovation',
          'Strategic Thinking',
          'Strategic Economic Leadership',
          'FinTech',
          'Banking and Finance',
          'Islamic Finance',
          'Artificial Intelligence',
        ],
      },
      {
        heading: 'Doctor of Business Administration – (DBA)',
        paragraphs: [
          'The DBA is designed for senior executives, consultants, entrepreneurs, and professionals who aim to combine advanced academic research with real-world strategic business impact.',
        ],
        facts: [
          { label: 'Total', detail: '180 ECTS' },
          {
            label: 'Core modules',
            detail:
              'including Research Methodology, Research Philosophy, Advanced Statistics, Strategic Management, Organizational Leadership, Advanced Accounting and Finance, and Multivariate Analysis',
          },
          { label: 'Thesis', detail: '50,000 words +/- 10%' },
        ],
      },
      {
        heading: 'DBA Specializations include',
        list: [
          'Strategic Management',
          'Accounting and Finance',
          'Human Resources Management',
          'Strategic Marketing',
          'Entrepreneurship and Innovation',
          'Logistics and Supply Chain Management',
          'Project Management',
          'Quality Management',
          'Digital Transformation',
          'Business Consultation',
        ],
      },
      {
        heading: 'Welcome to ECC Global',
        paragraphs: [
          'Study DBA, MBA & BBA Anytime, Anywhere – Internationally Accredited',
          'Want to Boost your career.',
          'Apply now and one of our academic advisors will help you achieve greatness.',
        ],
      },
      {
        heading: 'Main Office',
        facts: [
          {
            label: 'Address',
            detail:
              'Citibank building, 13th floor – Al Wasl Branch, Sheikh Rashid Road, Next to Wafi City – Dubai – United Arab Emirates',
          },
          { label: 'Phone + WhatsApp', detail: '+971558931013' },
          { label: 'Email', detail: 'info@ecc-group.ae' },
          { label: 'Website', detail: 'www.ecc-group.ae' },
        ],
      },
      {
        heading: 'Egypt Office (ECC GLOBAL)',
        facts: [
          {
            label: 'Address',
            detail: '14 DR Elshemy, 9th District, Nasr City, Cairo Governorate 11762',
          },
          { label: 'Phone', detail: '+201000498154' },
          { label: 'Email', detail: 'info@eccceg.com' },
          { label: 'Website', detail: 'www.eccceg.com' },
        ],
      },
    ],
    logos: [
      {
        src: '/media/images/partners/ecc-knowledge-group-logo.jpg',
        alt: 'ECC Knowledge Group logo featuring a teal shield emblem.',
      },
    ],
    gallery: [
      {
        src: 'https://ibas.edu.eu/wp-content/uploads/2024/07/Graduation-2024-800x669-1.jpg',
        alt: 'A smiling graduate in a blue-and-gold MBA sash has his mortarboard adjusted by a gowned official at ECC’s 2024 graduation ceremony.',
      },
      {
        src: 'https://ibas.edu.eu/wp-content/uploads/2024/07/Graduation-2024-800x712-1.jpg',
        alt: 'A graduate in cap and gown holds an IBAS Switzerland DBA Degree plaque beside a child and two gowned officials at ECC’s 2024 graduation ceremony.',
      },
      {
        src: 'https://ibas.edu.eu/wp-content/uploads/2024/07/Graduation-2024-800x642-1.jpg',
        alt: 'Green ECC Group gift bags lined up on leather armchairs in the graduation ceremony hall.',
      },
    ],
    websites: [
      { label: 'www.ecc-group.ae', href: 'http://www.ecc-group.ae' },
      { label: 'www.eccceg.com', href: 'http://www.eccceg.com' },
    ],
  },
  {
    name: 'International Business Academy of Turkey (IBAT)',
    country: 'Turkey',
    slug: 'international-business-academy-of-turkey',
    sections: [
      {
        paragraphs: [
          'International Business Academy of Turkey (IBAT) is an international educational institution based in Istanbul, Turkey.',
          'Such location gives the IBAT a strong positioning through international networking as well as flexibility and continuous improvement through the global exchange of experience and knowledge. IBAT is considered to be one of the most prominent capacity-building and consultancy service companies that acts as a bridge between internationally accredited and recognized awarding bodies in the Middle East, Africa, Western and Eastern Europe.',
          'IBAT provides a variety of services in areas of public and private institutional development such as education and capacity-building programs, human resource development, business development consultancies, marketing solutions, and supportive social development projects.',
        ],
      },
      {
        heading: 'Our Mission',
        paragraphs: [
          'To give our clients a competitive edge ensuring their professional growth and enhancing their effectiveness as well as to prepare them for global competitiveness by fostering high-quality educational & capacity building programs and offering a wide range of business & management solutions.',
        ],
      },
      {
        heading: 'Our Vision & Values',
        list: [
          'We believe that life-long education is a core factor of success.',
          'We think that a rapidly changing world calls for flexibility and new approach in education.',
          'We have an individual approach to each client by offering them a variety of tailor-made programs and services.',
          'We consider it crucially important to go international as by uniting our effort and sharing expertise with other countries, we can ensure a better future for new generations.',
          'We pride ourselves on our clients’ success as it is also our success.',
        ],
      },
    ],
    collaboration: {
      paragraphs: [
        'We offer the Standard Edition programs, IBAS international faculty teach the IBAS curriculum with support of tutors from our local learning center.',
        'IBAS Program Taught by IBAS International Faculty',
        'We offer online MBA & DBA programs, our students attend online classes taught by IBAS International Faculty with local tutor support at our learning center.',
      ],
      programmes: [
        'Master of Business Administration (MBA) – General – Online with IBAS International Faculty',
        'Doctor of Business Administration (DBA) – General – Online with IBAS International Faculty',
      ],
    },
    locations: ['Tercüman Sitesi Blok A8 Daire 1, Zeytinburnu / İstanbul, Turkey'],
    logos: [
      {
        src: '/media/images/partners/ibat-international-business-academy-logo.jpg',
        alt: 'Crest logo of IBAT, the International Business Academy of Turkey.',
      },
    ],
    gallery: [
      {
        src: '/media/images/partners/ibat-graduation-collage.webp',
        alt: 'Photo collage of IBAT graduation ceremonies and events arranged around the IBAT crest logo.',
      },
      {
        src: '/media/images/news/ibat-diploma-presentation-ceremony.webp',
        alt: 'Graduates and officials at an IBAT diploma presentation ceremony, with Syrian and Belarusian flags on the table.',
      },
    ],
    websites: [{ label: 'ibat-edu.com', href: 'http://ibat-edu.com/' }],
  },
  {
    name: 'Imperial Institute of Higher Education (IIHE)',
    country: 'Sri Lanka',
    slug: 'imperial-institute-of-higher-education',
    sections: [
      {
        paragraphs: [
          'Imperial Institute of Higher Education (IIHE) was established in 1996, starting its operations in a very modest scale, being associated with the prestigious University of Wales Trinity Saint David (UWTSD), U.K. in providing both undergraduate (B.Sc. Honours in Management) and post-graduate (MBA – Masters in Business Administration) level education. The association so far, has produced 600 B.Sc. graduates and over 1,000 post graduates. The majority of graduates completed their entire educational program in Sri Lanka, while a few did proceed to Wales, for their 3rd year studies, pursuing the “Transfer Study” route. The IIHE and University of Wales association is the longest, between a private tertiary education institute in Sri Lanka and a foreign university of international repute. During the past 25 years, IIHE has maintained a reputation for “Quality” in delivery of tertiary education. This was possible as IIHE is required to satisfy the rigorous demand of the Quality Assurance Agency of U.K. and the parent universities, UWTSD and UoS. Continuous evaluations are carried out by the universities to ensure that IIHE is conducting the programs in accordance with the agreed standards. At IIHE, the emphasis is on “Quality” in everything we do, and open opportunities for higher education to sync with our Mission to produce business leaders of future, which is increasingly sought by Sri Lankan corporate and SME sectors.',
        ],
      },
      {
        heading: 'Values',
        facts: [
          { label: 'Mutual Respect', detail: 'I commit to treat others the way I would like to be treated' },
          {
            label: 'Accountability',
            detail: 'I commit to take ownership for the task assigned to me & deliver results',
          },
          { label: 'Teamwork', detail: 'I commit to help others to achieve their goals' },
          {
            label: 'Positive Can-Do Attitude',
            detail: 'I commit to demonstrate positiveness in all my thoughts, words & actions',
          },
          {
            label: 'Do the Right Thing',
            detail: 'I commit to do right things focusing on company’s interests',
          },
          {
            label: 'Think Beyond the Box',
            detail: 'I commit to be innovative & creative in finding better solutions',
          },
        ],
      },
    ],
    collaboration: {
      paragraphs: [
        'We offer the Global Pathways Edition programs, our local teachers teach the IBAS curriculum at our local campuses.',
        'Same Program as IBAS – Same Curriculum as IBAS – Taught by Local Teachers – Approved by IBAS',
      ],
      programmes: [
        'Bachelor of Business Administration (BBA) – 3-year program',
        'Bachelor of Business Administration (BBA) – (Top-Up)',
        'Master of Business Administration (MBA)',
        'Master of Business Administration (MBA) – (Top-Up)',
        'DUAL MBA – IBAS International MBA in Innovation & Leadership and Perdana University Top-Up MBA',
        'Doctor of Business Administration (DBA)',
      ],
    },
    locations: ['No 5, Geethanjalee Place, Colombo 3, Sri Lanka'],
    logos: [
      { src: '/media/images/partners/iihe-logo-mark.png', alt: 'Red abstract IIHE logo mark.' },
    ],
    gallery: [
      {
        src: 'https://ibas.edu.eu/wp-content/uploads/2023/02/Building.jpeg',
        alt: 'The white multi-storey IIHE building in Colombo, its balconies and forecourt lined with potted plants.',
      },
    ],
    websites: [{ label: 'iihe.lk', href: 'https://iihe.lk' }],
  },
  {
    name: 'IIBM Institute of Business Management',
    country: 'India',
    slug: 'iibm-institute-of-business-management',
    sections: [
      {
        paragraphs: [
          'IIBM Institute of Business Management is an online professional management education training institution. Based in Delhi, the institute offers professional training and certification mostly in a management like a master program in business administration, etc.',
          'IIBM Institute of Business Management started its marketing operation from 2008, and it has gotten an immense good response from working professionals and new students as all IIBM courses are offered in a virtual platform that students can join the class anywhere, any moment, even while completing their regular studies or a full-time job. Besides teaching management courses, the institute has started to provide placement of their students so that they can earn immediately once they complete their online management courses.',
        ],
      },
      {
        heading: 'Vision',
        paragraphs: [
          'To contribute to India and the world by expanding online education, providing best valuable resources to industry and society and remain a source of pride.',
        ],
      },
      {
        heading: 'Mission',
        paragraphs: [
          'To help prepare outstanding educators and to advance the profession of education of our learners by providing user friendly support for seamless course access.',
        ],
      },
    ],
    collaboration: {
      paragraphs: [
        'We offer the Global Pathways Edition programs, our local teachers teach the IBAS curriculum at our local campuses.',
        'Same Program as IBAS – Same Curriculum as IBAS – Taught by Local Teachers – Approved by IBAS',
      ],
      programmes: [
        'Doctor of Business Administration (DBA) – General – Global Pathway Edition',
        'Master of Business Administration (MBA) – General – Global Pathway Edition',
      ],
    },
    locations: [
      'Main Office: Ithum IT Park, 612B, 6th Floor, Tower B, Sector 62, Noida-201301, Uttar Pradesh, India',
    ],
    logos: [
      {
        src: '/media/images/partners/iibm-institute-business-management-logo.png',
        alt: 'Logo of the IIBM Institute of Business Management.',
      },
    ],
    websites: [{ label: 'iibmindia.in', href: 'https://iibmindia.in/' }],
  },
  {
    name: 'GQEP Group of Schools',
    country: 'Philippines',
    slug: 'gqep-group-of-schools',
    sections: [
      {
        heading: 'Gates Professional Schools',
        paragraphs: [
          'Gates Professional Schools is a wholly owned subsidiary of Global Quality Education Providers, an educational entity with expertise in training curriculum and content development since 2005.',
          'In 2011, Gates Professional Schools was established with the vision of providing affordable quality 21st century education in the Philippines. Its first course offerings focused primarily on accounting and business programs.',
          'In 2013, in a landmark development, Gates introduced the first international blended BBA, MBA and DBA programs in the Philippines.',
          'Between 2019 to 2021, Gates launched a batch of new international program partnerships with St. Francis College New York, California University of Pennsylvania, International Business Academy of Switzerland and Monroe College (New York).',
        ],
      },
      {
        heading: 'Our Vision',
        paragraphs: [
          'We are the PREEMINENT institution of higher learning FOR EVERYONE and EVERYWHERE.',
        ],
      },
      {
        heading: 'Our Mission',
        paragraphs: [
          'We are a Leading Institution in Asia, Founded by Idealistic Academicians and Professionals, Committed to Education Beyond Boundaries, the Pursuit of Excellence, and the Development of the Global Citizen.',
        ],
      },
      {
        heading: 'The G.A.T.E.S. Imperatives',
        list: [
          'G – Gender Sensitivity',
          'A – Arts & Culture Appreciation',
          'T – Technology & Entrepreneurial Competence',
          'E – Environmental Consciousness',
          'S – Social Awareness & Responsiveness',
        ],
      },
      {
        heading: 'Gardner College',
        paragraphs: [
          'After 20+ years of being synonymous with quality I.T. education in the Philippines under the Informatics College brand name, Gardner College continues to answer the challenge of ASEAN integration and the move towards holistic and universal education through new international partnerships all over the world.',
          'These partnerships are consistent with a name change that reflects the school’s values promoting excellence, innovation and education for ALL (“educationem pro omnibus”).',
          'Also known as The Global School of Innovation, Gardner College pioneered in the Philippines a first-of-its-kind international dual degree program that combines academic competency with practical real-world application. Under the program, qualified Gardner graduates receive a Gardner diploma for their major of choice and an international bachelor’s diploma from the International Business Academy of Switzerland.',
        ],
      },
      {
        heading: 'Our Vision',
        paragraphs: ['We are the world leader in innovation and champion of universal education.'],
      },
      {
        heading: 'Our Mission',
        paragraphs: [
          'We are a premier international institution committed to excellence, innovation, the holistic development of the individual and the transformation of our society.',
        ],
      },
      {
        heading: 'Our Core Values',
        facts: [
          { label: 'Global', detail: 'Born in Asia in 1995, now in Europe and across the globe.' },
          {
            label: 'Excellent',
            detail: 'We strive to be the best in our curriculum, programs, facilities, faculty and staff.',
          },
          { label: 'Noble', detail: 'Meaningful success can only be achieved with integrity and honor.' },
          {
            label: 'Innovative',
            detail:
              'We are the pioneer of 21st century learning and the relentless pursuit of all things great and new.',
          },
          {
            label: 'Universal',
            detail:
              'We are the champion of education for ALL regardless of social class, gender, age, ethnicity, or physical, artistic and mental ability.',
          },
          {
            label: 'Selfless',
            detail: 'We mould our students to be men and women for others tasked to make a difference.',
          },
        ],
      },
    ],
    collaboration: {
      paragraphs: [
        'We offer the Global Pathways Edition programs, our local teachers teach the IBAS curriculum at our local campuses.',
        'Same Program as IBAS – Same Curriculum as IBAS – Taught by Local Faculty at our local campuses – Approved by IBAS',
        'We offer IBAS Standard online MBA & DBA programs, our students attend online classes taught by IBAS International Faculty with local tutor support at our campuses.',
      ],
      programmes: [
        'Bachelor of Business Administration (BBA) – General – Global Pathway Edition',
        'Master of Business Administration (MBA) – General – Online with IBAS International Faculty',
        'Doctor of Business Administration (DBA) – General – Online with IBAS International Faculty',
      ],
    },
    locations: [
      'Gates Professional Schools: 4th Floor, Kostka Bldg., Katipunan Avenue corner Rosa Alvero Street, Loyola Heights, Quezon City',
      'Gardner College – Diliman: 29 North Avenue, North EDSA, Diliman, 1105, Quezon City, Philippines',
      'Gardner College – Cainta: Felix Avenue corner Sta. Lucia Drive, Cainta, 1800 Rizal, Philippines',
      'Gardner College – Mindanao Avenue: Mindanao Avenue corner Road 3, Project 6, 1100 Quezon City, Philippines',
    ],
    logos: [
      {
        src: '/media/images/partners/gates-professional-schools-logo.png',
        alt: 'Gates Professional Schools crest logo with a crowned G emblem and the motto Excellentia et Pietas.',
      },
      {
        src: '/media/images/partners/gardner-college-logo.png',
        alt: 'Gardner College shield crest logo with the tagline The Global School of Innovation.',
      },
    ],
    gallery: [
      {
        src: '/media/images/partners/gates-professional-schools-building.webp',
        alt: 'Gates Professional Schools campus building exterior with rooftop signage.',
      },
      {
        src: '/media/images/partners/gates-professional-schools-computer-lab.webp',
        alt: 'Computer lab with rows of desktop workstations at Gates Professional Schools.',
      },
      {
        src: '/media/images/partners/gates-professional-schools-workshop.webp',
        alt: 'Presenter addressing attendees in a classroom workshop at Gates Professional Schools.',
      },
      {
        src: '/media/images/partners/gardner-college-building-exterior.webp',
        alt: 'Street-level view of the Gardner College campus building with signage.',
      },
      {
        src: '/media/images/partners/gardner-college-building-facade.webp',
        alt: 'Gardner College building facade with shield crest signage mounted above the entrance.',
      },
      {
        src: '/media/images/partners/gardner-college-lobby-reception.webp',
        alt: 'Gardner College reception lobby with branded backdrop and seating.',
      },
    ],
    websites: [
      { label: 'gates.edu.ph', href: 'https://gates.edu.ph/' },
      { label: 'gardner.edu.ph', href: 'https://gardner.edu.ph/' },
    ],
  },
  {
    name: 'Marwin Innovation Sdn Bhd (EDUK8U Grad School Asia)',
    country: 'Malaysia',
    slug: 'eduk8u-grad-school-asia',
    sections: [
      {
        paragraphs: [
          'At EDUK8U Grad School Asia, we equip students with the best possible opportunities through programmes delivered by highly skilled facilitators with strong industry links. We generate ideas that have an impact on the workplace. Moreover, we believe that students learn best through interactive debate. We encourage all opportunities to question current thinking. It’s this student-lecturer partnership that accounts for and accredits our value purpose.',
          'When you choose EDUK8U, you choose an experience designed to transform you into the person you envision yourself as. We encourage initiative, exploration, critical thought, and innovation. As a result, we will help you discover and realise your potential through our top graduate programs. We pride ourselves in our solid, innovative, and practical teachings, with the approach of introducing more innovative, customer-centric, and agile thinking into organisations.',
          'We also own and operate the International College of Queensland Australia CRICOS No: 01351B (ICQA) with EDUK8U Grad School Asia as a National Open College Network UK Accredited Centre; No: C002533 and our learning centres across Malaysia, Australia and South Pacific.',
        ],
      },
      {
        heading: 'Our Mission',
        paragraphs: [
          'To upskill our students with practical knowledge, sharing on the HOW and the art of delivery.',
        ],
      },
      {
        heading: 'Our Vision',
        paragraphs: [
          'We aim to equip students with the best possible opportunities through programmes delivered by highly-skilled academic staff with strong industry links.',
        ],
      },
    ],
    collaboration: {
      paragraphs: [
        'We offer IBAS Standard Edition programs, we offer the IBAS curriculum at our local learning center.',
        'IBAS Program – IBAS Curriculum – Taught by IBAS International Faculty',
        'We offer online DBA programs, our students attend online classes taught by IBAS Faculty with local tutor support at our learning center.',
      ],
      programmes: [
        'Doctor of Business Administration (DBA) – General – Online with IBAS International Faculty',
      ],
      after: [
        'We’re thrilled to announce the launch of the Doctor of Business Administration (DBA) program through International Business Academy of Switzerland (IBAS) approved learning center, EDUK8U Grad School Asia!',
        'This groundbreaking collaboration was officially signed by Martin Nielsen, Chief Executive Officer of My Global World Education Group, and Roy Prasad, Chief Executive Officer of EDUK8U Grad School Asia.',
        'EDUK8U Grad School Asia proudly serves as an approved learning center for the prestigious International Business Academy of Switzerland (IBAS), ensuring the highest standards of education.',
      ],
    },
    locations: [
      'SO-20-07, Menara 1, 3, Jalan Bangsar, KL Eco City, 59200, Kuala Lumpur, Federal Territory of Kuala Lumpur',
    ],
    logos: [
      {
        src: '/media/images/partners/eduk8u-grad-school-asia-logo.png',
        alt: 'Logo of EDUK8U — Upskilling and Reskilling with Grad School Asia.',
      },
    ],
    gallery: [
      {
        src: '/media/images/graduation-events/dba-launch-signing-ceremony.webp',
        alt: 'Two men holding signed partnership agreements at the IBAS and EDUK8U DBA launch signing ceremony.',
      },
      {
        src: '/media/images/graduation-events/eduk8u-grad-school-asia-graduation-group-photo.webp',
        alt: 'Large group of graduates in caps and gowns holding certificates at an EDUK8U Grad School Asia graduation ceremony.',
      },
    ],
    websites: [{ label: 'eduk8u.com', href: 'https://www.eduk8u.com/' }],
  },
  {
    name: 'My Global World Sdn Bhd',
    country: 'Malaysia',
    slug: 'my-global-world',
    sections: [
      {
        paragraphs: [
          'My Global World Sdn Bhd Malaysia is the sister company of My Global World Education Group. It is currently focused on recruiting students for the Education Group itself. The office is located in the heart of Kuala Lumpur the capital of Malaysia.',
          'With the aim of providing affordable and quality education, My Global World Sdn Bhd Malaysia strives to be amongst the best. My Global World Malaysia is actively recruiting Malaysian based students who are interested to pursue their Masters and Doctorate in Business for the schools affiliated under the education group.',
          'The Malaysia head office aims to have a good network of students across the region pursuing their dreams in the Business field. The dedicated team of the education group makes great effort in providing the best counselling and services to all our students.',
          'Ms Hema Doraisamy is currently managing My Global World Sdn Bhd Malaysia and is also partners with Mr Martin Nielsen the CEO of My Global Education Group.',
        ],
      },
    ],
    locations: [
      'Head Office: Level 4, 17-3, Jalan 15/48A, Sentul Raya Boulevard, 51000 Kuala Lumpur, Wilayah Persekutuan Kuala Lumpur, Malaysia.',
    ],
    logos: [
      {
        src: '/media/images/partners/my-global-world-education-group-logo.jpg',
        alt: 'Logo of My Global World Education Group.',
      },
    ],
    gallery: [
      {
        src: 'https://ibas.edu.eu/wp-content/uploads/2021/06/Hema-picture-for-work-683x1024.jpg',
        alt: 'Studio portrait of Ms Hema Doraisamy, who manages My Global World Sdn Bhd Malaysia, seated in a navy suit.',
      },
    ],
  },
  {
    name: 'Myanmar Commercial College',
    country: 'Myanmar',
    slug: 'myanmar-commercial-college',
    sections: [
      {
        paragraphs: [
          'Myanmar Commercial College (MCC) is a top business institute located in Mandalay, Myanmar. Myanmar Commercial College (MCC) aspires to developing young business-minded professionals!',
          'MCC has partners in United Kingdom, France and Switzerland and provides higher certificates, diplomas, bachelor’s degrees, master’s degrees and doctoral programs to meet the educational requirements of the country.',
          'MCC experienced faculty specialise in developing businesses and organizations that are needed in Myanmar, to improve business and management skills of professionals. Led by faculty, it is established for the benefit of systematic teaching and social sharing.',
        ],
      },
      {
        heading: 'Vision',
        paragraphs: [
          'To become a qualified institution based on excellent education and enhanced learning environment.',
        ],
      },
      {
        heading: 'Mission',
        paragraphs: [
          'To provide a high quality, relevant education enabling the affordable learning environment for learners to achieve their goals and ethical business leaders with a global perspective through excellent education.',
        ],
      },
    ],
    collaboration: {
      paragraphs: [
        'We offer Franchised programs, our local teachers teach the IBAS curriculum at our local campuses.',
        'Same Program as IBAS – Same Curriculum as IBAS – Taught by Local Teachers – Approved by IBAS',
      ],
      programmes: ['Doctor of Business Administration (DBA)'],
    },
    locations: ['64th Street, Between 27th and 28th Street, Mandalay, Myanmar'],
    logos: [
      {
        src: '/media/images/partners/myanmar-commercial-college-logo.png',
        alt: 'Logo of Myanmar Commercial College.',
      },
    ],
    gallery: [
      {
        src: '/media/images/graduation-events/mcu-graduation-ceremony-hall-group-photo.webp',
        alt: 'Large group of graduates in caps and gowns posing in a hall at a Myanmar Commercial University graduation ceremony.',
      },
      {
        src: '/media/images/graduation-events/mcu-graduation-ceremony-stage-photo.webp',
        alt: 'Graduates and academic staff in gowns and sashes posing on stage at a Myanmar Commercial University graduation ceremony.',
      },
      {
        src: '/media/images/news/myanmar-commercial-university-campus-visit.webp',
        alt: 'IBAS staff with Myanmar Commercial University students and staff outside the MCU campus building.',
      },
    ],
    websites: [{ label: 'mcu.edu.mm', href: 'https://mcu.edu.mm/' }],
  },
  {
    name: 'National Management Academy',
    country: 'Myanmar',
    slug: 'national-management-academy',
    sections: [
      {
        paragraphs: [
          'National Management Academy (Parami International College) is a training center under the management of Professional Academics and Principal, established in 2018 and it has now a capacity of over four thousand students.',
        ],
      },
      {
        heading: 'Vision',
        paragraphs: [
          'A community of lifelong learners that demonstrate the knowledge, skills, and values required for productive Myanmar citizenship.',
        ],
      },
      {
        heading: 'Mission',
        list: [
          'To be a recognized institution in Myanmar.',
          'To provide the educational services that can assure the quality of our students to the Myanmar job industry.',
          'To enhance the development and welfare of community.',
          'To develop the required competence and skills needed that can be applied in the business environment.',
        ],
      },
      {
        heading: 'Locations',
        paragraphs: [
          'A management institute situated in three different locations in Yangon at Sanchaung, Hlaing, and Tamwe Townships and operating over 8 branches.',
        ],
      },
      {
        heading: 'In the news',
        paragraphs: [
          'International Business Academy of Switzerland (IBAS) Administration Visits National Management Academy (NMA) in Myanmar',
          'Mr. Martin Nielsen, IBAS CEO expressed his gratitude of the great work NMA was doing in providing high quality education for all students enrolled in IBAS programs. Ms. Anna Phan, IBAS Registrar conducted a QA audit of the admissions, registrars and academic departments.',
          'May 2023',
        ],
      },
    ],
    collaboration: {
      paragraphs: [
        'We offer the Global Pathways Edition programs, our local teachers teach the IBAS curriculum at our local campuses.',
        'Same Program as IBAS – Same Curriculum as IBAS – Taught by Local Teachers – Approved by IBAS',
      ],
      programmes: ['Master of Business Administration (MBA) – General – Global Pathway Edition'],
    },
    locations: [
      'Head Office: Room 12 (A), Dagon Center II Residence, Pyay Road, Maynigone, Sanchaung Township, Yangon, Myanmar',
      'NMA Maynigone: No. (F-8) First Floor, Inside of Dagon Center 1, Sanchaung Township, Yangon',
      'NMA Hledan: No. (96) Insein Road, 5th Floor, Inside of Gamone Pwint-Sanyeik Nyein, Yangon',
    ],
    logos: [
      {
        src: '/media/images/partners/national-management-academy-logo.png',
        alt: 'National Management Academy logo featuring a shield crest.',
      },
    ],
    gallery: [
      {
        src: '/media/images/graduation-events/national-management-academy-graduation-ceremony.webp',
        alt: 'Graduates in caps and gowns posing on stage at the National Management Academy third graduation ceremony.',
      },
      {
        src: '/media/images/campus/national-management-academy-campus-entrance.webp',
        alt: 'Students walking up the entrance steps of a National Management Academy campus building.',
      },
      {
        src: '/media/images/campus/national-management-academy-dagon-center-campus.webp',
        alt: 'Collage of photos showing the National Management Academy Dagon Center 1 campus building.',
      },
      {
        src: '/media/images/news/nma-partnership-certificate-presentation.webp',
        alt: 'Representatives presenting a framed certificate outside the National Management Academy building.',
      },
    ],
    websites: [{ label: 'nmaeducation.com', href: 'https://www.nmaeducation.com/' }],
  },
  {
    name: 'PSM International College',
    country: 'Myanmar',
    slug: 'psm-international-college',
    sections: [
      {
        paragraphs: [
          'PSM International College is a skill development training centre and consulting firm focused on teaching, learning, education and delivers world class business management training programs such as HR, Marketing, Banking, Finance, Leadership and Personal Development training to young adults, working professionals, and business executives. It also supports consulting services to startups and SMEs.',
          'Our programmes, services and quality of teaching are intended to make best use of students’ potential. Moreover, the faculty members focus on preparing the students with scholarly and professional foundation demanded today’s and tomorrow’s professional careers and personal development.',
          'At PSM International College, we count quality than quantity.',
        ],
      },
    ],
    collaboration: {
      paragraphs: [
        'We act as your local learning center in Myanmar.',
        'Same Program as IBAS – Same Curriculum as IBAS – Supported by Local Teachers – Awarded by IBAS',
      ],
      programmes: ['Doctor of Business Administration (DBA)'],
    },
    locations: [
      'Main Office: No. (4056), 3rd Floor, Taw Win Centre Shopping Mall, Pyay Road, Dagon Township, Yangon, Myanmar',
    ],
    logos: [
      {
        src: '/media/images/partners/psm-international-college-logo.png',
        alt: 'Logo of PSM International College.',
      },
    ],
    gallery: [
      {
        src: 'https://ibas.edu.eu/wp-content/uploads/2026/02/PSM-college-image-800x804.png',
        alt: 'PSM International College MBA poster — a montage of students and graduates around the PSM Graduate School crest.',
      },
    ],
    websites: [{ label: 'mypsm.psm.edu.mm', href: 'https://mypsm.psm.edu.mm' }],
  },
];

/** Slug lookup for the detail page (undefined for unknown slugs). */
export function getLearningCenterPartner(
  slug: string | undefined,
): LearningCenterPartner | undefined {
  return LEARNING_CENTER_PARTNERS.find((partner) => partner.slug === slug);
}

/** Derived reach figures for the section's proof strip. */
export const LEARNING_CENTER_STATS = {
  partners: LEARNING_CENTER_PARTNERS.length, // 10
  countries: new Set(LEARNING_CENTER_PARTNERS.map((p) => p.country)).size, // 7
} as const;

/* ═══════════════════════════════════════════════════════════════════════════
 * 3 · AGENTS  (source: /about/agents/)
 *     Personal third-party contacts intentionally omitted; enquiries route
 *     through IBAS. Only the official IBAS role alias is surfaced.
 * ═══════════════════════════════════════════════════════════════════════════ */

export const AGENTS_LEDE =
  'Authorised admissions representatives who guide prospective students to IBAS in their own regions — with counselling, course selection and application support close to home.';

export interface Agent {
  name: string;
  location: string;
  /** One-line description of the agent’s role, from the source. */
  focus: string;
  /** Official IBAS role-alias email, where one is published (optional). */
  email?: string;
}

export const AGENTS: readonly Agent[] = [
  {
    name: 'Al Mansour Company for International Admissions',
    location: 'Baghdad, Iraq',
    focus: 'Assists students wanting to study abroad, from first enquiry through to admission.',
  },
  {
    name: 'BCES',
    location: 'Gurgaon, India',
    focus:
      'Counselling, selecting the right course and institute, and support with admissions, scholarships and visas — established 1997.',
    email: 'india@ibas.edu.eu',
  },
  {
    name: 'BKI — Bach Khoa Training Institute',
    location: 'Ho Chi Minh City, Vietnam',
    focus: 'Training and coaching for business leaders, alongside management consulting.',
  },
];

/** How students without a local agent reach IBAS directly. */
export const AGENTS_CONTACT_NOTE =
  'In regions without a local representative, our admissions team is always the direct route in.';

export const AGENTS_ADMISSIONS_EMAIL = 'admissions@ibas.edu.eu';

/* ═══════════════════════════════════════════════════════════════════════════
 * 4 · BECOME A PARTNER  (source: /about/learning-center-partners/ CTA)
 * ═══════════════════════════════════════════════════════════════════════════ */

export const BECOME_PARTNER_HEADING = 'Partner with IBAS';

/** Faithful to the source CTA on the Learning Center Partners page. */
export const BECOME_PARTNER_BODY =
  'If your institution is interested in becoming a Learning Center Partner, get in touch and a representative from our Strategic Planning Office will contact you.';

/** Small "why partner" points — framing only, no invented facts. */
export interface PartnerBenefit {
  title: string;
  detail: string;
  Icon: LucideIcon;
}

export const PARTNER_BENEFITS: readonly PartnerBenefit[] = [
  {
    title: 'Swiss-quality curriculum',
    detail: 'Deliver internationally oriented, accredited business programmes designed by IBAS.',
    Icon: Award,
  },
  {
    title: 'A global network',
    detail: 'Join partners and centres across Europe, the Middle East and Asia.',
    Icon: Compass,
  },
  {
    title: 'Shared academic support',
    detail: 'Work alongside IBAS faculty and student-support structures.',
    Icon: BookOpen,
  },
];

/* Icons re-exported for section use where a component needs a generic glyph. */
export const PARTNER_ICONS = {
  academic: GraduationCap,
  learningCenter: Building2,
  agents: Users,
  globe: MapPin,
  handshake: Handshake,
  spark: Sparkles,
  rocket: Rocket,
} as const;
